/**
 * Getting a real store into the register: the opening stock, and every stock
 * count after it.
 *
 * A stores system is only worth anything on the day somebody's actual shelves
 * are in it, and no storekeeper is going to type four hundred lines into a
 * form. This reads what they already have — the spreadsheet the store has been
 * run from, or rows pasted out of it — and says what it WOULD do before
 * anything is saved.
 *
 * ── the one distinction that matters ────────────────────────────────────
 *
 * When a file gives a quantity for a line the register already holds, that is
 * either:
 *
 *   an OPENING BALANCE — "this is what we have; the register was empty or
 *   wrong and this is the truth now". It is not evidence about anything.
 *
 *   or a STOCK COUNT — "we walked the shelves and this is what we found".
 *   That IS evidence: it is the only thing the count-accuracy figure is
 *   computed from, and a difference it finds is a real discrepancy somebody
 *   should explain.
 *
 * Loading an opening balance as though it were a count would score the store
 * at whatever the spreadsheet happened to say and call it accuracy measured.
 * So the caller must say which it is, the dialog asks in those words, and the
 * movements written carry the distinction.
 *
 * ── what it will not do ─────────────────────────────────────────────────
 *
 * A blank cell leaves the register's value alone; an import never erases a
 * recorded cost or supplier because a column was empty. A quantity it cannot
 * read is an error on that row, never a zero — a zero would read as "we
 * counted and found none", which is the opposite of "nobody could read this".
 * A row with an error is not applied, and it is reported with its line number
 * so it can be found in the file.
 *
 * Pure. The dialog gathers, this decides.
 */
import { t } from "../i18n";
import { parseTable } from "./fleetEdit";
import { categoriesOf, categoryLabel, heldAt, type StockItem, type StoreCategory, type StoresRegister } from "./stores";

/* ── the columns it understands ───────────────────────────────────────── */

export type IntakeField =
  | "sku"
  | "name"
  | "category"
  | "uom"
  | "qty"
  | "min"
  | "max"
  | "unitCost"
  | "leadDays"
  | "supplier"
  | "location"
  | "bin"
  | "critical"
  | "partNumber"
  | "manufacturer"
  | "expiresAt"
  | "note";

interface ColumnDef {
  key: IntakeField;
  label: string;
  /** what a heading may be called, lower-cased and stripped of punctuation */
  names: string[];
}

/**
 * The names real stores spreadsheets use, not only the ones this app uses.
 * A column this list does not know is reported as ignored rather than
 * silently dropped, so somebody can see their "Bin Location" went nowhere.
 */
export const INTAKE_COLUMNS: ColumnDef[] = [
  { key: "sku", label: "Catalog number", names: ["sku", "code", "number", "part no", "partno", "item", "item code", "item no", "material", "stock code", "catalogue number", "catalog number"] },
  { key: "name", label: "Name", names: ["name", "description", "item name", "part", "part name", "material description"] },
  { key: "category", label: "Category", names: ["category", "cat", "group", "class", "type"] },
  { key: "uom", label: "Unit", names: ["uom", "unit", "unit of measure", "units", "measure"] },
  { key: "qty", label: "Quantity", names: ["qty", "quantity", "on hand", "onhand", "stock", "in stock", "balance", "count", "counted", "soh", "stock on hand"] },
  { key: "min", label: "Reorder point", names: ["min", "minimum", "reorder point", "reorder level", "rop", "min qty", "safety stock"] },
  { key: "max", label: "Maximum", names: ["max", "maximum", "max qty", "max level", "order up to"] },
  { key: "unitCost", label: "Unit cost", names: ["unit cost", "cost", "price", "unit price", "rate", "value", "cost each"] },
  { key: "leadDays", label: "Lead time (days)", names: ["lead", "lead days", "lead time", "lead time days", "delivery days"] },
  { key: "supplier", label: "Supplier", names: ["supplier", "vendor", "manufacturer supplier", "source", "supplied by"] },
  { key: "location", label: "Store", names: ["store", "location", "warehouse", "store code", "stores"] },
  { key: "bin", label: "Bin", names: ["bin", "shelf", "rack", "bin location", "position"] },
  { key: "critical", label: "Critical spare", names: ["critical", "critical spare", "criticality", "vital"] },
  { key: "partNumber", label: "Part number", names: ["part number", "manufacturer part", "mpn", "oem", "oem number"] },
  { key: "manufacturer", label: "Manufacturer", names: ["make", "manufacturer", "brand", "oem make"] },
  { key: "expiresAt", label: "Shelf life ends", names: ["expiry", "expires", "expiry date", "shelf life", "use by", "best before"] },
  { key: "note", label: "Note", names: ["note", "notes", "remark", "remarks", "comment"] },
];

const tidy = (s: string) => s.trim().toLowerCase().replace(/[._/\\-]+/g, " ").replace(/\s+/g, " ");

export function columnFor(heading: string): IntakeField | null {
  const h = tidy(heading);
  if (!h) return null;
  for (const c of INTAKE_COLUMNS) if (c.names.includes(h)) return c.key;
  /* a heading like "On hand (each)" — match on what comes before the bracket */
  const bare = tidy(h.replace(/\(.*$/, ""));
  for (const c of INTAKE_COLUMNS) if (c.names.includes(bare)) return c.key;
  return null;
}

export const FIELD_LABEL: Record<IntakeField, string> = Object.fromEntries(
  INTAKE_COLUMNS.map((c) => [c.key, c.label]),
) as Record<IntakeField, string>;

/* ── reading a cell ───────────────────────────────────────────────────── */

/** A number as stores spreadsheets write them: 1,200 · 1 200 · "12.5" · SAR 340. */
function readNumber(raw: string): { value?: number; why?: string } {
  const s = raw.trim();
  if (!s) return {};
  const cleaned = s.replace(/[^\d.,-]/g, "").replace(/\s/g, "");
  if (!cleaned) return { why: t("no number in it") };
  /* 1,234.50 — the comma groups; 1.234,50 — the comma is the decimal */
  const normalised =
    /,\d{1,2}$/.test(cleaned) && !/\.\d/.test(cleaned)
      ? cleaned.replace(/\./g, "").replace(",", ".")
      : cleaned.replace(/,/g, "");
  const n = Number(normalised);
  if (!Number.isFinite(n)) return { why: t("not a number") };
  return { value: n };
}

const YES = ["y", "yes", "true", "1", "critical", "vital", "نعم"];

function readCategory(raw: string, reg: Pick<StoresRegister, "categories">): { value?: StoreCategory; why?: string } {
  const s = tidy(raw);
  if (!s) return {};
  const all = categoriesOf(reg);
  for (const c of all) {
    if (c.key === s || tidy(c.label) === s) return { value: c.key };
  }
  /* a word from the category's own label — "electrical", "paint", "AGL" */
  for (const c of all) {
    if (tidy(c.label).split(" ").includes(s)) return { value: c.key };
  }
  return { why: t("not a category this register holds") };
}

function readDate(raw: string): { value?: string; why?: string } {
  const s = raw.trim();
  if (!s) return {};
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return { value: s };
  const dmy = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s);
  if (dmy) {
    const [, d, m, y] = dmy;
    return { value: `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}` };
  }
  const ms = Date.parse(s);
  if (Number.isNaN(ms)) return { why: t("not a date this can read — use YYYY-MM-DD") };
  return { value: new Date(ms).toISOString().slice(0, 10) };
}

/* ── the plan ─────────────────────────────────────────────────────────── */

export interface IntakeIssue {
  field?: IntakeField;
  message: string;
  /** the cell as written, where the problem is that it cannot be read */
  value?: string;
}

/**
 * A row with fewer cells than the heading row.
 *
 * One missing comma shifts every value after it along by a column, and
 * nothing about the result looks wrong: a price lands in the quantity column
 * and the register books 385 lamps because somebody's spreadsheet had a blank
 * cell it did not write out. There is no way to know WHICH column was left
 * out, so this does not guess — it refuses the row and says what to fix.
 *
 * Only short rows are refused. A row with spare cells on the end has nothing
 * to shift.
 */
function raggedRow(cells: string[], heads: string[]): IntakeIssue | null {
  if (cells.length >= heads.length) return null;
  return {
    message: t(
      "this row has {has} cells and the heading row has {wants} — one missing comma shifts every value after it into the wrong column",
      { has: cells.length, wants: heads.length },
    ),
  };
}

export interface IntakeChange {
  field: IntakeField;
  label: string;
  before: string;
  after: string;
}

export interface IntakeRow {
  /** the line in the file, counting the heading as line 1 */
  line: number;
  /** what the same line holds in OTHER stores, which this row does not touch */
  elsewhere?: { location: string; qty: number }[];
  sku: string;
  name: string;
  status: "new" | "update" | "same" | "error";
  errors: IntakeIssue[];
  /** the existing line, where there is one */
  existing?: StockItem;
  changes: IntakeChange[];
  /** the quantity the file gives, where it gives one */
  qty?: number;
  location?: string;
  bin?: string;
  /** what the line would be, for a new one */
  item?: Omit<StockItem, "id">;
}

export interface IntakePlan {
  rows: IntakeRow[];
  /** headings the reader did not recognise, named so nobody assumes they landed */
  ignored: string[];
  /** which heading filled each field, so a wrong guess can be seen */
  columns: Partial<Record<IntakeField, string>>;
  /** set when the file cannot be read at all */
  problem?: string;
  counts: { new: number; update: number; same: number; error: number };
}

export interface IntakeDefaults {
  /** the store the quantities land in, where the file names none */
  location: string;
  /** that store's name, for the change a person reads */
  locationLabel?: string;
  /** the unit to assume where the file gives none */
  uom?: string;
}

/**
 * What an intake WOULD do. Nothing is saved and the register is not touched.
 *
 * Only two columns are required: the catalogue number, because it is the key
 * everything else hangs off, and — for a line the register has never seen — a
 * name, because a catalogue of numbers with no names is not a catalogue.
 */
export function planIntake(reg: StoresRegister, text: string, defaults: IntakeDefaults): IntakePlan {
  const empty = { rows: [], ignored: [], columns: {}, counts: { new: 0, update: 0, same: 0, error: 0 } };
  const table = parseTable(text);
  if (table.length < 2) {
    return { ...empty, problem: t("The file needs a heading row and at least one line beneath it.") };
  }
  const heads = table[0];
  const cols = heads.map(columnFor);
  const ignored = heads.filter((h, i) => h.trim() && cols[i] === null);
  const columns: Partial<Record<IntakeField, string>> = {};
  cols.forEach((c, i) => {
    if (c && columns[c] === undefined) columns[c] = heads[i];
  });
  if (!cols.includes("sku")) {
    return {
      ...empty,
      ignored,
      problem: t(
        "No catalog number column. Every line needs one — a heading called SKU, code, item number or part number.",
      ),
    };
  }

  const bySku = new Map(reg.items.map((i) => [i.sku.toLowerCase(), i]));
  const seen = new Map<string, number>();
  const rows: IntakeRow[] = [];

  for (let r = 1; r < table.length; r += 1) {
    const cells = table[r];
    if (cells.every((c) => !c.trim())) continue;
    const at = (f: IntakeField): string => {
      const i = cols.indexOf(f);
      return i < 0 ? "" : (cells[i] ?? "").trim();
    };
    const line = r + 1;
    const errors: IntakeIssue[] = [];
    const ragged = raggedRow(cells, heads);
    if (ragged) errors.push(ragged);
    const sku = at("sku");
    if (!sku) {
      rows.push({ line, sku: "", name: at("name"), status: "error", errors: [{ field: "sku", message: t("no catalog number") }], changes: [] });
      continue;
    }
    const dup = seen.get(sku.toLowerCase());
    if (dup !== undefined) {
      rows.push({
        line,
        sku,
        name: at("name"),
        status: "error",
        errors: [{ field: "sku", message: t("the same catalog number is already on line {n} of this file", { n: dup }) }],
        changes: [],
      });
      continue;
    }
    seen.set(sku.toLowerCase(), line);

    const num = (f: IntakeField) => {
      const raw = at(f);
      const read = readNumber(raw);
      if (read.why) errors.push({ field: f, message: read.why, value: raw });
      return read.value;
    };
    const qty = num("qty");
    const min = num("min");
    const max = num("max");
    const unitCost = num("unitCost");
    const leadDays = num("leadDays");
    if (qty !== undefined && qty < 0) errors.push({ field: "qty", message: t("a quantity cannot be negative"), value: at("qty") });

    const cat = readCategory(at("category"), reg);
    if (cat.why) errors.push({ field: "category", message: cat.why, value: at("category") });
    const expiry = readDate(at("expiresAt"));
    if (expiry.why) errors.push({ field: "expiresAt", message: expiry.why, value: at("expiresAt") });

    const criticalRaw = at("critical");
    const critical = criticalRaw ? YES.includes(tidy(criticalRaw)) : undefined;

    const location = at("location") || defaults.location;
    const bin = at("bin") || undefined;
    const existing = bySku.get(sku.toLowerCase());
    const name = at("name") || existing?.name || "";

    if (!existing && !name) {
      errors.push({ field: "name", message: t("a line the catalog has never seen needs a name") });
    }
    if (!existing && cat.value === undefined && !at("category")) {
      /* not an error: a line with no category is filed as a general consumable and said so */
      errors.push({ field: "category", message: t("no category given — it will be filed under general consumables"), value: "" });
    }

    const changes: IntakeChange[] = [];
    const note = (field: IntakeField, before: string | number | undefined, after: string | number | undefined) => {
      if (after === undefined || after === "") return;
      const b = before === undefined || before === "" ? t("not recorded") : String(before);
      const a = String(after);
      if (b !== a) changes.push({ field, label: FIELD_LABEL[field], before: b, after: a });
    };

    if (existing) {
      note("name", existing.name, at("name") || undefined);
      note("category", categoryLabel(existing.category, reg), cat.value ? categoryLabel(cat.value, reg) : undefined);
      note("uom", existing.uom, at("uom") || undefined);
      note("min", existing.min, min);
      note("max", existing.max, max);
      note("unitCost", existing.unitCost, unitCost);
      note("leadDays", existing.leadDays, leadDays);
      note("supplier", existing.supplier, at("supplier") || undefined);
      note("partNumber", existing.partNumber, at("partNumber") || undefined);
      note("manufacturer", existing.manufacturer, at("manufacturer") || undefined);
      note("expiresAt", existing.expiresAt, expiry.value);
      note("critical", existing.critical ? t("yes") : t("no"), critical === undefined ? undefined : critical ? t("yes") : t("no"));
      /*
       * Against what is held AT THIS STORE, not against the total.
       *
       * A file's quantity is a quantity somewhere — the store its own column
       * names, or the one chosen in the dialog. Comparing it with the total
       * across every bin is how a count of 58 for the main store, on a line
       * that also has 64 in the AGL store, previewed as "76 → 58" and then
       * left the register holding 122.
       */
      const here = heldAt(existing, location);
      if (qty !== undefined && qty !== here) {
        changes.push({
          field: "qty",
          label: t("Quantity in {store}", {
            store: location === defaults.location ? (defaults.locationLabel ?? location) : location,
          }),
          before: String(here),
          after: String(qty),
        });
      }
    }

    const blocking = errors.filter(
      (e) => e.field === undefined || e.field !== "category" || e.message.includes("not a category"),
    );
    const status: IntakeRow["status"] = blocking.length > 0 && (!existing || blocking.some((e) => e.field !== "category"))
      ? "error"
      : existing
        ? changes.length > 0
          ? "update"
          : "same"
        : "new";

    rows.push({
      line,
      sku,
      name,
      status,
      errors,
      existing,
      elsewhere: existing
        ? existing.bins
            .filter((b) => b.location !== location && b.onHand > 0)
            .map((b) => ({ location: b.location, qty: b.onHand }))
        : undefined,
      changes,
      qty,
      location,
      bin,
      item:
        existing || status === "error"
          ? undefined
          : {
              sku,
              name,
              category: cat.value ?? "consumable",
              uom: at("uom") || defaults.uom || "each",
              unitCost,
              manufacturer: at("manufacturer") || undefined,
              partNumber: at("partNumber") || undefined,
              min: min ?? 0,
              max: max ?? 0,
              leadDays,
              critical: critical ? { why: t("marked critical in the file this was loaded from") } : undefined,
              supplier: at("supplier") || undefined,
              bins: [{ location, bin, onHand: qty ?? 0 }],
              expiresAt: expiry.value,
              note: at("note") || undefined,
            },
    });
  }

  return {
    rows,
    ignored,
    columns,
    counts: {
      new: rows.filter((r) => r.status === "new").length,
      update: rows.filter((r) => r.status === "update").length,
      same: rows.filter((r) => r.status === "same").length,
      error: rows.filter((r) => r.status === "error").length,
    },
  };
}

/** One line of plain English about what the plan would do. */
export function intakeSummary(plan: IntakePlan): string {
  if (plan.problem) return plan.problem;
  const parts: string[] = [];
  if (plan.counts.new) parts.push(t("{n} new to the catalog", { n: plan.counts.new }));
  if (plan.counts.update) parts.push(t("{n} changed", { n: plan.counts.update }));
  if (plan.counts.same) parts.push(t("{n} already as recorded", { n: plan.counts.same }));
  if (plan.counts.error) parts.push(t("{n} that cannot be read and will be left out", { n: plan.counts.error }));
  return parts.length ? parts.join(", ") : t("Nothing in the file to apply.");
}


/* ── a delivery, read from a spreadsheet ──────────────────────────────── */

/**
 * Booking in what arrived, from the supplier's own list.
 *
 * A delivery of forty lines is forty rows on a delivery note, and typing them
 * into a form one at a time is how a store stops booking deliveries in at all
 * — after which the register is wrong about everything and nobody trusts it.
 *
 * Every line ADDS to what is on the shelf. That is the difference between
 * this and a stock load: a load says what the shelf holds, a delivery says
 * what has just been put on it. Confusing the two would either lose the
 * existing stock or double it, so they are separate functions with separate
 * words on the screen.
 */
export interface ReceiptRow {
  line: number;
  sku: string;
  name: string;
  qty?: number;
  unitCost?: number;
  location: string;
  bin?: string;
  status: "ready" | "error" | "new";
  errors: IntakeIssue[];
  /** for a part the catalogue has never held: what would be created for it */
  item?: Omit<StockItem, "id">;
  /** what the shelf holds now, and what it would hold after */
  before?: number;
  after?: number;
  /** the order line this answers, where the receipt is against an order */
  outstanding?: number;
  /** what the same line holds in OTHER stores, which this delivery does not touch */
  elsewhere?: { location: string; qty: number }[];
}

export interface ReceiptPlan {
  rows: ReceiptRow[];
  ignored: string[];
  problem?: string;
  counts: { ready: number; error: number; new: number };
  /** the total quantity that would go on the shelf */
  units: number;
}

/**
 * Plan a delivery from a table. `orderNumber` narrows it to an order's own
 * lines, and then a quantity beyond what is outstanding is refused here
 * rather than at the end — the same rule `receiveDelivery` holds, said at the
 * point where it can still be corrected.
 */
/**
 * Where a delivered line should land when nobody has said.
 *
 * Into the store that already holds it. A storekeeper booking in ten
 * photo-eyes means them to join the eight already on the terminal shelf, not
 * to start a second holding in the main store — and a register that splits a
 * line across two stores because of a dropdown nobody touched is a register
 * that has to be corrected by hand afterwards.
 *
 * Where the line is held in more than one store, or in none, there is no
 * obvious answer and the chosen store stands.
 */
function homeStore(item: StockItem | undefined, fallback: string): string {
  if (!item) return fallback;
  const places = [...new Set(item.bins.map((b) => b.location))];
  return places.length === 1 ? places[0] : fallback;
}

export function planReceipt(
  reg: StoresRegister,
  text: string,
  defaults: IntakeDefaults & { orderNumber?: string; storeChosen?: boolean; allowNew?: boolean },
): ReceiptPlan {
  const empty = { rows: [], ignored: [], counts: { ready: 0, error: 0, new: 0 }, units: 0 };
  const table = parseTable(text);
  if (table.length < 2) {
    return { ...empty, problem: t("The delivery needs a heading row and at least one line beneath it.") };
  }
  const heads = table[0];
  const cols = heads.map(columnFor);
  const ignored = heads.filter((h, i) => h.trim() && cols[i] === null);
  if (!cols.includes("sku")) {
    return { ...empty, ignored, problem: t("No catalog number column. Every line of a delivery needs one.") };
  }
  if (!cols.includes("qty")) {
    return { ...empty, ignored, problem: t("No quantity column. A delivery with no quantities is a packing list, not a receipt.") };
  }

  const order = defaults.orderNumber ? reg.orders.find((o) => o.number === defaults.orderNumber) : undefined;
  const rows: ReceiptRow[] = [];
  const takenPerSku = new Map<string, number>();

  for (let r = 1; r < table.length; r += 1) {
    const cells = table[r];
    if (cells.every((c) => !c.trim())) continue;
    const at = (f: IntakeField): string => {
      const i = cols.indexOf(f);
      return i < 0 ? "" : (cells[i] ?? "").trim();
    };
    const line = r + 1;
    const errors: IntakeIssue[] = [];
    const ragged = raggedRow(cells, heads);
    if (ragged) errors.push(ragged);
    const sku = at("sku");
    const item = reg.items.find((i) => i.sku.toLowerCase() === sku.toLowerCase());
    /*
     * A part the catalogue has never held.
     *
     * It arrives anyway — a contractor brings a part nobody ordered, an
     * emergency purchase is made off-catalogue — and a stores system that can
     * only receive what it already knows about forces the storekeeper to put
     * it on the shelf without recording it. So it can be created here, as
     * long as the row says what it IS: a delivery line with a number and no
     * name would create a catalogue entry nobody could identify later.
     */
    const named = at("name");
    const makeNew = !item && Boolean(sku) && defaults.allowNew === true;
    if (!sku) errors.push({ field: "sku", message: t("no catalog number") });
    else if (!item && !defaults.allowNew) {
      errors.push({
        field: "sku",
        message: t("not in the catalog — add the line first, or load it with the stock"),
        value: sku,
      });
    } else if (makeNew && !named) {
      errors.push({ field: "name", message: t("not in the catalog, and the row gives no name to create it with"), value: sku });
    }
    const cat = makeNew ? readCategory(at("category"), reg) : { value: undefined, why: undefined };
    if (cat.why) errors.push({ field: "category", message: cat.why, value: at("category") });

    const readQty = readNumber(at("qty"));
    if (readQty.why) errors.push({ field: "qty", message: readQty.why, value: at("qty") });
    else if (readQty.value !== undefined && readQty.value <= 0) {
      errors.push({ field: "qty", message: t("a delivered quantity must be more than nothing"), value: at("qty") });
    }
    const readCost = readNumber(at("unitCost"));
    if (readCost.why) errors.push({ field: "unitCost", message: readCost.why, value: at("unitCost") });

    /* the file's own column first, then where the line already lives, then the choice */
    const location =
      at("location") || (defaults.storeChosen ? defaults.location : homeStore(item, defaults.location));
    const qty = readQty.value;

    let outstanding: number | undefined;
    if (order && item) {
      const ol = order.lines.find((l) => l.sku === item.sku);
      if (!ol) {
        errors.push({ field: "sku", message: t("not a line on {number}", { number: order.number }), value: sku });
      } else {
        const taken = takenPerSku.get(item.sku) ?? 0;
        outstanding = ol.qty - ol.received - taken;
        if (qty !== undefined && qty > outstanding) {
          errors.push({
            field: "qty",
            message: t("{number} has {owed} outstanding on this line", { number: order.number, owed: outstanding }),
            value: String(qty),
          });
        } else if (qty !== undefined) {
          takenPerSku.set(item.sku, taken + qty);
        }
      }
    }

    const before = item ? heldAt(item, location) : makeNew ? 0 : undefined;
    const status: ReceiptRow["status"] = errors.length ? "error" : makeNew ? "new" : "ready";
    rows.push({
      line,
      sku,
      name: item?.name ?? named,
      item:
        status === "new"
          ? {
              sku,
              name: named,
              category: cat.value ?? "consumable",
              uom: at("uom") || defaults.uom || "each",
              unitCost: readCost.value,
              min: readNumber(at("min")).value ?? 0,
              max: readNumber(at("max")).value ?? 0,
              supplier: at("supplier") || undefined,
              bins: [],
            }
          : undefined,
      qty,
      unitCost: readCost.value,
      location,
      bin: at("bin") || undefined,
      status,
      errors,
      before,
      after: before !== undefined && qty !== undefined ? before + qty : undefined,
      outstanding,
      elsewhere: item
        ? item.bins.filter((b) => b.location !== location && b.onHand > 0).map((b) => ({ location: b.location, qty: b.onHand }))
        : undefined,
    });
  }

  return {
    rows,
    ignored,
    counts: {
      ready: rows.filter((r) => r.status === "ready").length,
      error: rows.filter((r) => r.status === "error").length,
      new: rows.filter((r) => r.status === "new").length,
    },
    units: rows.filter((r) => r.status !== "error").reduce((n, r) => n + (r.qty ?? 0), 0),
  };
}

export function receiptSummary(plan: ReceiptPlan): string {
  if (plan.problem) return plan.problem;
  const parts: string[] = [];
  if (plan.counts.ready + plan.counts.new) {
    parts.push(t("{n} lines to book in, {units} units in all", { n: plan.counts.ready + plan.counts.new, units: plan.units }));
  }
  if (plan.counts.new) parts.push(t("{n} of them new to the catalog", { n: plan.counts.new }));
  if (plan.counts.error) parts.push(t("{n} that cannot be booked in and will be left out", { n: plan.counts.error }));
  return parts.length ? parts.join(", ") : t("Nothing in the delivery to book in.");
}
